import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';
import AppDatePicker from 'components/dateTime/AppDatePicker';
import spinnerTitle from 'components/others/spinnerTitle';
import ObjectStatusHistoryTable from './ObjectStatusHistoryTable';
import { COMMENT_MAX_LENGTH, type ObjectCloseFormValues, type ObjectCloseMode, type ObjectStatusHistoryRow } from './model';

interface ObjectCloseFormProps {
    mode: ObjectCloseMode;
    values: ObjectCloseFormValues;
    setFieldValue: (field: string, value: unknown) => void;
    handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
    isSubmitting: boolean;
    finishedDateError?: string;
    commentError?: string;
    history: ObjectStatusHistoryRow[];
    historyLoading: boolean;
    autofocus?: boolean;
    onlySale: boolean;
    onlyService: boolean;
}

const ObjectCloseForm = ({
    mode,
    values,
    setFieldValue,
    handleSubmit,
    isSubmitting,
    finishedDateError,
    commentError,
    history,
    historyLoading,
    autofocus = true,
    onlySale,
    onlyService,
}: ObjectCloseFormProps) => {
    const { t } = useTranslation(['Objects', 'common']);
    const refComment = useRef<HTMLTextAreaElement>(null);
    const comment = String(values.comment || '');
    const closeLabel = onlyService
        ? t('toolServiceSaleFinished')
        : onlySale
            ? t('toolSaleFinished')
            : t('toolObjectFinished');
    const cancelLabel = onlyService
        ? t('toolServiceSaleCancel')
        : onlySale
            ? t('toolSaleCancel')
            : t('toolObjectCancel');
    const canSave = mode === 'close'
        ? Boolean(values.finishedDate) && values.finishConfirm && !isSubmitting
        : Boolean(comment.trim()) && !isSubmitting;

    useEffect(() => {
        if (!autofocus || mode === 'close') return;
        setTimeout(() => refComment.current?.focus(), 100);
    }, [autofocus, mode]);

    return (
        <Form className="d-flex flex-column h-100" onSubmit={handleSubmit}>
            <div className="off-create-team-form d-flex flex-column h-100">
                <div className="off-create-team-content px-3">
                    {mode === 'close' ? (
                        <>
                            <span className="mb-3 d-block">{t('infoObjectFinished')}</span>
                            <Form.Group className="mb-3">
                                <Form.Label htmlFor="object-close-date" className="text-muted">
                                    {t('labelCloseDate')}
                                </Form.Label>
                                <AppDatePicker
                                    id="object-close-date"
                                    value={values.finishedDate}
                                    onChange={(iso) => setFieldValue('finishedDate', iso)}
                                />
                                {finishedDateError ? (
                                    <div className="invalid-feedback d-block">{finishedDateError}</div>
                                ) : null}
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="switch"
                                    id="object-close-confirm"
                                    checked={values.finishConfirm}
                                    onChange={(e) => setFieldValue('finishConfirm', e.target.checked)}
                                    label={t('labelFinishConfirm')}
                                />
                                <Form.Text className="text-muted d-block mt-1">
                                    {t('descFinishConfirm')}
                                </Form.Text>
                            </Form.Group>
                        </>
                    ) : (
                        <>
                            <span className="mb-3 d-block">
                                {mode === 'cancel' ? t('infoObjectCancel') : t('objectClose.infoReturn')}
                            </span>
                            <Form.Group className="mb-3">
                                <label className="form-label text-muted" htmlFor="object-close-reason">
                                    {t('objectClose.labelReason')}
                                </label>
                                <Form.Control
                                    as="textarea"
                                    id="object-close-reason"
                                    ref={refComment}
                                    rows={3}
                                    value={comment}
                                    maxLength={COMMENT_MAX_LENGTH}
                                    onChange={(e) =>
                                        setFieldValue(
                                            'comment',
                                            e.target.value.slice(0, COMMENT_MAX_LENGTH),
                                        )
                                    }
                                />
                                {commentError ? (
                                    <div className="invalid-feedback d-block">{commentError}</div>
                                ) : null}
                                <div className="form-text text-end">
                                    {comment.length} / {COMMENT_MAX_LENGTH}
                                </div>
                            </Form.Group>
                        </>
                    )}

                    <div className="mb-2">
                        <div className="text-muted small mb-2">{t('objectClose.historyTitle')}</div>
                        <ObjectStatusHistoryTable rows={history} loading={historyLoading} />
                    </div>
                </div>

                <div className="p-3 offcanvas-footer off-create-team-footer">
                    <div className="d-flex justify-content-end">
                        <Button
                            type="submit"
                            variant={mode === 'cancel' ? 'danger' : mode === 'return' ? 'info' : 'success'}
                            disabled={!canSave}
                            className="min100px"
                        >
                            {spinnerTitle(
                                mode === 'close'
                                    ? closeLabel
                                    : mode === 'cancel'
                                        ? cancelLabel
                                        : t('objectClose.btnReturn'),
                                isSubmitting,
                                t('common:buttons.saving'),
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </Form>
    );
};

export default ObjectCloseForm;
